<template>
  <teleport to="body">
    <v-navigation-drawer
      v-model="isOpen"
      location="right"
      temporary
      width="580"
      :scrim="true"
      class="pathway-details-sidebar"
    >
      <!-- Top Navigation Bar -->
      <div
        class="d-flex justify-space-between align-center pa-4"
        style="position: sticky; top: 0; background-color: white; z-index: 10"
      >
        <!-- Back Button -->
        <v-btn
          variant="text"
          size="large"
          class="text-none font-weight-bold pl-0 pl-md-4"
          style="color: #000000"
          @click="closeSidebar"
        >
          <v-icon size="large" class="">mdi-chevron-left</v-icon>
          Back
        </v-btn>

        <!-- Register for Pathway Button (only show if not enrolled) -->
        <v-btn
          v-if="!isEnrolled"
          color="#ffc627"
          variant="flat"
          rounded
          class="text-none font-weight-bold px-4"
          style="color: #000000"
          @click="handleEnrollInPathway"
        >
          Register for pathway
        </v-btn>
      </div>

      <!-- Hero Image -->
      <v-img :src="pathwayImage" height="250" cover :transition="false" />

      <!-- Pathway Title and Details -->
      <div class="pa-6 pa-md-8 px-md-16">
        <h1 class="font-weight-bold mb-6 mt-4" style="font-size: 36px; line-height: 1.2">
          Pathway to {{ pathwayName }}
        </h1>

        <!-- Degree Description -->
        <div v-if="pathwayDescription" class="mb-6">
          <h3 class="font-weight-bold mb-3" style="font-size: 18px">Degree Description</h3>
          <p class="text-body-3" style="color: #000000; line-height: 1.6" v-html="pathwayDescription" />
        </div>

        <!-- Degree Location -->
        <div class="mb-6">
          <h3 class="font-weight-bold mb-3" style="font-size: 18px">Degree Location</h3>
          <p class="text-body-3" style="color: #000000">
            {{ pathwayLocation }}
          </p>
        </div>

        <!-- Admission Requirements -->
        <div v-if="admissionRequirements" class="mt-8">
          <h3 class="font-weight-bold mb-4" style="font-size: 24px; color: #000000">Admission Requirements</h3>
          <div class="text-body-3" style="color: #000000; line-height: 1.6" v-html="admissionRequirements" />
        </div>
      </div>
    </v-navigation-drawer>
  </teleport>
</template>

<script>
import { defineComponent } from 'vue';
import {
  DEFAULT_PATHWAY_IMAGE,
  isOnCampusHiddenPartner,
  stripOnCampusLocation,
  isEngineeringAdditionalRequirementsPathway,
  ENGINEERING_ADDITIONAL_REQUIREMENTS_NOTICE_HTML
} from '@/constants/pathways';
import { DataLayerMixin } from '@/mixins/dataLayerMixin';

/**
 * Sanitize HTML content to prevent XSS attacks
 * Only allows safe tags and removes potentially dangerous attributes
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') return '';

  // Create a temporary element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Define allowed tags
  const allowedTags = [
    'p',
    'br',
    'strong',
    'em',
    'b',
    'i',
    'u',
    'ul',
    'ol',
    'li',
    'a',
    'span',
    'div',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6'
  ];

  // Define allowed attributes per tag
  const allowedAttributes = {
    a: ['href', 'target', 'rel'],
    span: ['class'],
    div: ['class'],
    p: ['class']
  };

  // Recursive function to sanitize nodes
  function sanitizeNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const tagName = node.tagName.toLowerCase();

    // If tag is not allowed, only return text content
    if (!allowedTags.includes(tagName)) {
      return node.textContent;
    }

    // Build sanitized element
    const sanitizedEl = document.createElement(tagName);

    // Copy only allowed attributes
    const tagAllowedAttrs = allowedAttributes[tagName] || [];
    for (const attr of tagAllowedAttrs) {
      if (node.hasAttribute(attr)) {
        let value = node.getAttribute(attr);
        // For href, only allow http, https, and mailto protocols
        if (attr === 'href') {
          if (!/^(https?:|mailto:|\/|#)/.test(value)) {
            continue; // Skip dangerous protocols like javascript:
          }
        }
        sanitizedEl.setAttribute(attr, value);
      }
    }

    // For links, enforce security attributes
    if (tagName === 'a') {
      sanitizedEl.setAttribute('rel', 'noopener noreferrer');
    }

    // Recursively sanitize children
    for (const child of node.childNodes) {
      const sanitizedChild = sanitizeNode(child);
      if (typeof sanitizedChild === 'string') {
        sanitizedEl.appendChild(document.createTextNode(sanitizedChild));
      } else if (sanitizedChild) {
        sanitizedEl.appendChild(sanitizedChild);
      }
    }

    return sanitizedEl;
  }

  // Sanitize all children
  const result = document.createElement('div');
  for (const child of tempDiv.childNodes) {
    const sanitizedChild = sanitizeNode(child);
    if (typeof sanitizedChild === 'string') {
      result.appendChild(document.createTextNode(sanitizedChild));
    } else if (sanitizedChild) {
      result.appendChild(sanitizedChild);
    }
  }

  return result.innerHTML;
}

export default defineComponent({
  name: 'PathwayDetailsSidebar',

  mixins: [DataLayerMixin],

  emits: ['update:modelValue', 'enroll-pathway', 'pathwayEnrolled', 'enrollPathway'],

  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    pathwayId: {
      type: [String, Number],
      default: null
    },
    isEnrolled: {
      type: Boolean,
      default: false
    },
    pathwayData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      scrollPosition: 0
    };
  },

  computed: {
    isOpen: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    pathwayName() {
      return this.pathwayData?.pathwayName || '';
    },
    pathwayImage() {
      return this.pathwayData?.imageUrl || DEFAULT_PATHWAY_IMAGE;
    },
    pathwayDescription() {
      return sanitizeHtml(this.pathwayData?.pathwayDescription || '');
    },
    pathwayLocation() {
      const location = this.pathwayData?.location || 'Online, On Campus';
      // ULC-2919: corporate partners do not see on-campus offerings — show online-only when an online option exists.
      if (isOnCampusHiddenPartner(this.$conn?.learnerData?._partnerName)) {
        return stripOnCampusLocation(location);
      }
      return location;
    },
    admissionRequirements() {
      let requirements = this.pathwayData?.admissionRequirements || '';
      // Append the additional-requirements notice for the in-scope engineering degrees.
      if (isEngineeringAdditionalRequirementsPathway(this.pathwayData?.pathwayName)) {
        requirements += ENGINEERING_ADDITIONAL_REQUIREMENTS_NOTICE_HTML;
      }
      return sanitizeHtml(requirements);
    }
  },

  watch: {
    isOpen: {
      handler(newValue) {
        if (newValue) {
          // Save current scroll position
          this.scrollPosition = window.scrollY || window.pageYOffset;

          // Add class to lock scroll at current position
          document.body.classList.add('pathway-sidebar-open');
          document.body.style.top = `-${this.scrollPosition}px`;

          // Reset sidebar scroll to top
          this.$nextTick(() => {
            const drawerContent = document.querySelector('.pathway-details-sidebar .v-navigation-drawer__content');
            if (drawerContent) {
              drawerContent.scrollTop = 0;
            }
          });
        } else {
          // Remove class and restore scroll
          document.body.classList.remove('pathway-sidebar-open');
          document.body.style.top = '';

          // Restore scroll position instantly (no smooth scroll)
          window.scrollTo({ top: this.scrollPosition, left: 0, behavior: 'instant' });
        }
      },
      immediate: true
    }
  },

  methods: {
    closeSidebar() {
      this.isOpen = false;
    },

    handleEnrollInPathway() {
      if (!this.pathwayId) {
        return;
      }

      // GA tracking for enroll in pathway button click
      this.asuoDataLayerClick('PathwayDetailsSidebar', '0');

      // Emit event to parent (PathwayRecommendationCard) to trigger enrollment
      this.$emit('enroll-pathway');
    }
  },

  beforeUnmount() {
    // Cleanup: remove class and restore styles when component is destroyed
    document.body.classList.remove('pathway-sidebar-open');
    document.body.style.top = '';
  }
});
</script>

<style>
/* Target the navigation drawer */
.pathway-details-sidebar {
  background-color: white;
}

/* Override link colors to ASU maroon */
.pathway-details-sidebar a {
  color: #8c1d40 !important;
  text-decoration: underline;
  font-weight: 600;
}

/* Lock body scroll when pathway sidebar is open */
body.pathway-sidebar-open {
  position: fixed !important;
  width: 100% !important;
  overflow: hidden !important;
}
</style>
